import StyledInput from '@/components/styled/StyledInput';
import StyledRadioGroup from '@/components/styled/StyledRadioGroup';
import { Label } from '@/components/ui/label';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { Control, useWatch } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import AgencyOverlayPlaceholderField from './AgencyOverlayPlaceholderField';

interface EstimateAdditionalMarginFieldsProps {
  control: Control<EstimateFormData>;
  disabled?: boolean;
  muted?: boolean;
  hideValues?: boolean;
  /** When the margin-rate field sits in the same card, repeat the section label. */
  showSectionLabel?: boolean;
}

/**
 * Shared layout for additional margin: inline radios (Fixe / En %) + full-width value
 * input. Used on the agency link (editable) and TO view (read-only indicator).
 */
const EstimateAdditionalMarginFields = ({
  control,
  disabled = false,
  muted = false,
  hideValues = false,
  showSectionLabel = false,
}: EstimateAdditionalMarginFieldsProps) => {
  const { t } = useTranslation();

  const additionalMarginType = useWatch({
    control,
    name: 'agencyAdditionalMarginType',
  });

  return (
    <div className="flex flex-1 flex-col gap-2">
      {showSectionLabel && (
        <Label className={muted ? 'text-gray-500' : 'text-gray-700'}>
          {t('estimates.agencyView.additionalMargin.title')}
        </Label>
      )}
      {hideValues ? (
        <div className="flex w-full flex-row items-center gap-x-4 gap-y-2">
          <AgencyOverlayPlaceholderField
            muted={muted}
            className="max-w-[9rem] shrink-0"
          />
          <AgencyOverlayPlaceholderField muted={muted} className="min-w-0 flex-1" />
        </div>
      ) : (
        <div className="flex w-full flex-row items-center gap-x-4 gap-y-2">
          <div className="flex h-10 shrink-0 items-center">
            <StyledRadioGroup
              control={control}
              name="agencyAdditionalMarginType"
              inline
              disabled={disabled}
              className="shrink-0"
              options={[
                {
                  label: t('estimates.agencyView.additionalMargin.fixed'),
                  value: 'FIXED',
                },
                {
                  label: t('estimates.agencyView.additionalMargin.percent'),
                  value: 'PERCENT',
                },
              ]}
            />
          </div>
          <div className="flex h-10 min-w-0 flex-1 items-center">
            <StyledInput
              control={control}
              name="agencyAdditionalMarginValue"
              type="number"
              hideNumberFieldArrows
              disabled={disabled}
              className="w-full gap-0"
              placeholder={additionalMarginType === 'PERCENT' ? '%' : '€'}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimateAdditionalMarginFields;
